/**
 * Node-specific converters for converting React Flow node data to legacy HTML format
 * Each converter handles the specific requirements and transformations for its node type
 */
import { NODE_TYPE_TO_KIND_MAP } from '../../shared/constants/index'
import type {
  AutoAttendantHtml,
  CallDistributionHtml,
  ChangeCallerIdHtml,
  DayNightHtml,
  ExternalNumberHtml,
  InteractiveWebhookHtml,
  SmsHtml,
  TransferRolloverHtml,
  WebhookHtml,
} from '../../shared/legacyNodes'
import type { DayNightNodeData, NodeData } from '../../shared/reactFlowTypes'
import { NodeType } from '../../shared/reactFlowTypes'
import { extractNumericId } from '../../shared/uuidUtils'
import { ensureRequiredHtmlFields } from '../htmlFieldHelpers'
import { baseConvertNodeDataToHtml } from './baseConverter'
import {
  booleanToLegacyNumber,
  convertOptionsToChildren,
  deleteAdmin3OnlyFields,
  handleMinimizedField,
  normalizeOptionId,
} from './utils'

/**
 * Convert SMS notification node data to legacy HTML format
 */
export function convertSmsNotificationNodeData(
  nodeData: NodeData,
  nodeId?: string,
  messagingGroups?: Array<{
    id: number
    phone_numbers?: Array<{
      number?: string
      number_for_display?: string
      is_primary_sms?: boolean
    }>
  }>,
): SmsHtml {
  const html = baseConvertNodeDataToHtml(
    nodeData as Record<string, unknown>,
    NodeType.SmsNotification,
    nodeId,
  )

  // CRITICAL: Convert sendFrom (messaging group ID) to phone number for legacy
  // Legacy expects sms_number to be a phone number, not a messaging group ID
  // Legacy code (line 7703): empty strings are converted to null
  // Logic: val() != '' && val() != undefined ? "value" : null
  let smsNumberValue: string | null = null

  if ('sendFrom' in nodeData) {
    const sendFromValue = nodeData.sendFrom

    // Handle empty string - convert to null per legacy behavior
    if (sendFromValue === '' || !sendFromValue) {
      smsNumberValue = null
    } else {
      const sendFromStr = String(sendFromValue)

      // Try to find messaging group by ID
      if (messagingGroups && messagingGroups.length > 0) {
        const group = messagingGroups.find((g) => String(g.id) === sendFromStr)

        if (group && group.phone_numbers && group.phone_numbers.length > 0) {
          // Find primary SMS phone number, or fall back to first phone number
          const primarySms = group.phone_numbers.find(
            (phone) => phone.is_primary_sms === true,
          )
          const phoneToUse = primarySms || group.phone_numbers[0]

          // CRITICAL: Legacy expects unformatted number (e.g., "5126348845"), not formatted (e.g., "(512) 634-8845")
          // Legacy matches by value.number === selectedNumber (line 3329 in edit_switchboard.php)
          // and uses value.number as the option value (line 3333)
          smsNumberValue = phoneToUse.number || null
        } else {
          // If group not found, check if sendFrom is already a phone number (legacy data)
          // In this case, keep it as-is
          smsNumberValue = sendFromStr
        }
      } else {
        // No messaging groups available, assume sendFrom is already a phone number (legacy data)
        smsNumberValue = sendFromStr
      }
    }
  } else if ('send_from' in nodeData && nodeData.send_from) {
    // Handle send_from as fallback (shouldn't happen, but safety net)
    const sendFromValue = nodeData.send_from
    smsNumberValue = sendFromValue === '' ? null : String(sendFromValue)
  }
  // If sendFrom is null, undefined, or empty string, smsNumberValue stays null

  html['sms_number'] = smsNumberValue

  // CRITICAL: Convert messageType to option per legacy spec
  // Legacy saves option as actual value ("1", "2", "3", "4") when selected, not always null
  // Map messageType to option: "Missed call" -> "1", "Incoming call" -> "2", "Voicemail" -> "3", "Custom" -> "4"
  if ('messageType' in nodeData && nodeData.messageType) {
    const messageType = String(nodeData.messageType)
    if (messageType === 'Missed call') {
      html['option'] = '1'
    } else if (messageType === 'Incoming call') {
      html['option'] = '2'
    } else if (messageType === 'Voicemail') {
      html['option'] = '3'
    } else if (messageType === 'Custom') {
      html['option'] = '4'
    } else {
      html['option'] = null
    }
  } else {
    html['option'] = null
  }

  // CRITICAL: Convert phoneNumber to sms_recipient per legacy spec
  // Legacy saves sms_recipient as actual value when provided, not always null
  // phoneNumber maps to sms_recipient (legacy field name)
  // Also check for smsRecipient as alternate field name (for backward compatibility)
  if ('phoneNumber' in nodeData && nodeData.phoneNumber) {
    html['sms_recipient'] = String(nodeData.phoneNumber)
  } else if ('smsRecipient' in nodeData && nodeData.smsRecipient) {
    html['sms_recipient'] = String(nodeData.smsRecipient)
  } else {
    html['sms_recipient'] = null
  }

  // Remove phone_number if it was added by baseConvertNodeDataToHtml (shouldn't happen now, but safety net)
  if ('phone_number' in html) {
    delete html['phone_number']
  }
  // Remove message_type if it was added by baseConvertNodeDataToHtml (shouldn't happen now, but safety net)
  if ('message_type' in html) {
    delete html['message_type']
  }

  // Remove send_from from HTML object (it's Admin3-only field, should have been converted to sms_number)
  delete html['send_from']

  if (
    'autoReplyToCaller' in nodeData &&
    typeof nodeData.autoReplyToCaller === 'boolean'
  ) {
    html['auto_reply'] = booleanToLegacyNumber(nodeData.autoReplyToCaller)
  }

  handleMinimizedField(nodeData, html)

  const kind = NODE_TYPE_TO_KIND_MAP[NodeType.SmsNotification]
  return ensureRequiredHtmlFields(kind, html) as SmsHtml
}

/**
 * Convert external transfer/external number node data to legacy HTML format
 */
export function convertExternalTransferNodeData(
  nodeData: NodeData,
  nodeId?: string,
): ExternalNumberHtml {
  const html = baseConvertNodeDataToHtml(
    nodeData as Record<string, unknown>,
    NodeType.ExternalTransfer,
    nodeId,
  )

  if (
    'requireOneToConnect' in nodeData &&
    typeof nodeData.requireOneToConnect === 'boolean'
  ) {
    html['press_1'] = booleanToLegacyNumber(nodeData.requireOneToConnect)
  }

  // CRITICAL: Legacy saves BOTH number and phone_number_id independently
  // They are NOT mutually exclusive - legacy saves both if both are present
  // Legacy saves number: val != '' ? '"val"' : 'null' (line 7793)
  // Legacy saves phone_number_id: val != '' ? '"val"' : 'null' (line 7805)
  // The pass_through_caller_id checkbox only affects UI (disables dropdown), not save logic
  //
  // IMPORTANT: Always save both fields if they exist - don't treat them as mutually exclusive
  const phoneNumberRaw =
    'phoneNumber' in nodeData ? nodeData.phoneNumber : undefined
  const phoneNumberValue =
    phoneNumberRaw !== null &&
    phoneNumberRaw !== undefined &&
    String(phoneNumberRaw).trim() !== ''
      ? String(phoneNumberRaw).trim()
      : null

  // Save phoneNumber as number field (only if present and non-empty)
  if (phoneNumberValue) {
    html['number'] = phoneNumberValue
  }
  // Note: When phoneNumber is empty, don't include the number field at all
  // This matches legacy behavior where empty fields may be omitted
  delete html['phone_number'] // Remove any phone_number field from baseConverter

  // Save phoneNumberId as phone_number_id field (always, if present)
  if ('phoneNumberId' in nodeData) {
    const phoneNumberIdValue = nodeData.phoneNumberId
    if (
      phoneNumberIdValue !== null &&
      phoneNumberIdValue !== undefined &&
      phoneNumberIdValue !== ''
    ) {
      html['phone_number_id'] = String(phoneNumberIdValue)
      delete html['caller_id'] // Remove deprecated caller_id
    } else {
      html['phone_number_id'] = null
    }
  } else {
    html['phone_number_id'] = null
  }

  // Save transferCompany as transfer_company (selected company from dropdown).
  // Empty/unselected ("--") is stored as JSON null.
  const transferCompanyRaw =
    'transferCompany' in nodeData ? nodeData.transferCompany : undefined
  html['transfer_company'] =
    transferCompanyRaw !== null &&
    transferCompanyRaw !== undefined &&
    String(transferCompanyRaw).trim() !== ''
      ? String(transferCompanyRaw)
      : null

  handleMinimizedField(nodeData, html)

  const kind = NODE_TYPE_TO_KIND_MAP[NodeType.ExternalTransfer]
  const result = ensureRequiredHtmlFields(kind, html) as ExternalNumberHtml

  // Remove Admin3-only field
  deleteAdmin3OnlyFields(result, ['require_one_to_connect'])

  return result
}

/**
 * Convert auto attendant node data to legacy HTML format
 */
export function convertAutoAttendantNodeData(
  nodeData: NodeData,
  nodeId?: string,
): AutoAttendantHtml {
  const html = baseConvertNodeDataToHtml(
    nodeData as Record<string, unknown>,
    NodeType.AutoAttendant,
    nodeId,
  )

  // CRITICAL: Preserve option IDs from existing HTML children (for round-trip scenarios)
  // If html already has children from a previous conversion, preserve their IDs
  // This ensures option IDs remain stable across round-trip conversions
  // Validate existing children array structure before preserving IDs
  let existingChildren =
    'children' in html && Array.isArray(html.children)
      ? (html.children as Array<Record<string, unknown>>)
      : null

  // Validate children[0] is {} (legacy spec requirement)
  // If structure is invalid, don't preserve IDs to avoid corruption
  if (
    existingChildren &&
    existingChildren.length > 0 &&
    Object.keys(existingChildren[0] || {}).length > 0
  ) {
    // Don't preserve IDs if structure is invalid (children[0] must be empty {})
    existingChildren = null
  }

  if ('options' in nodeData && Array.isArray(nodeData.options)) {
    const options = nodeData.options as Array<Record<string, unknown>>
    html['children'] = convertOptionsToChildren(
      options,
      nodeData as Record<string, unknown>,
      nodeId,
      (option, child, index) => {
        if ('digit' in option && option.digit) {
          child['press_id'] = String(option.digit)
        }
        // Preserve option ID from existing children array if available (for round-trip)
        // Match by index (existingChildren[0] is {}, so use index + 1)
        // Priority order: option.id > existingChildren[index+1].id > generated ID
        // CRITICAL: Check both !child['id'] AND !option.id to avoid overwriting option.id
        // convertOptionsToChildren will check option.id after fieldMapper, so we must not set
        // from existingChildren if option.id exists
        if (
          existingChildren &&
          index !== undefined &&
          existingChildren.length > index + 1 &&
          existingChildren[index + 1] &&
          typeof existingChildren[index + 1] === 'object' &&
          'id' in existingChildren[index + 1] &&
          !child['id'] && // Only preserve if child doesn't already have an ID
          !('id' in option && option.id) // Don't preserve if option has explicit ID (takes priority)
        ) {
          // CRITICAL: Normalize option ID to remove node type prefix for auto_attendant
          // Legacy Admin's save condition splits by '_' and expects numeric node ID as first part
          const existingId = String(existingChildren[index + 1].id)
          child['id'] = normalizeOptionId(existingId, 'auto_attendant')
        }
      },
      'auto_attendant',
    )
    // Remove options array after converting to children
    delete html['options']
  }

  // CRITICAL: Explicitly preserve sound_clip_id if it exists in nodeData
  // This ensures sound_clip_id is not lost during conversion
  if ('soundClipId' in nodeData) {
    const soundClipIdValue = nodeData.soundClipId
    if (
      soundClipIdValue !== null &&
      soundClipIdValue !== undefined &&
      soundClipIdValue !== ''
    ) {
      html['sound_clip_id'] = String(soundClipIdValue)
    } else {
      html['sound_clip_id'] = null
    }
  } else if ('sound_clip_id' in html) {
    // Already set by baseConverter, ensure it's not lost
    // Keep existing value (baseConverter should have handled it)
  } else {
    html['sound_clip_id'] = null
  }

  const kind = NODE_TYPE_TO_KIND_MAP['auto_attendant']
  const result = ensureRequiredHtmlFields(kind, html) as AutoAttendantHtml
  // Remove Admin3-only fields
  deleteAdmin3OnlyFields(result, ['options', 'extension_id'])
  // Note: line_id is NOT Admin3-only - it's a required legacy field that should always be null
  // ensureRequiredHtmlFields should add it, but ensure it's JSON null (not string "null")
  // Defensive check: ensure line_id exists and is JSON null (explicitly set it)
  result['line_id'] = null
  // Also ensure children array exists and starts with empty object if options was empty
  if (!('children' in result) || !Array.isArray(result['children'])) {
    result['children'] = [{}]
  } else if (
    Array.isArray(result['children']) &&
    (result['children'].length === 0 ||
      !(result['children'][0] && typeof result['children'][0] === 'object'))
  ) {
    result['children'] = [{}, ...result['children']]
  }
  return result
}

/**
 * Convert call distribution node data to legacy HTML format
 */
export function convertCallDistributionNodeData(
  nodeData: NodeData,
  nodeId?: string,
): CallDistributionHtml {
  const html = baseConvertNodeDataToHtml(
    nodeData as Record<string, unknown>,
    NodeType.CallDistribution,
    nodeId,
  )

  // CRITICAL: Preserve option IDs from existing HTML children (for round-trip scenarios)
  // If html already has children from a previous conversion, preserve their IDs
  // This ensures option IDs remain stable across round-trip conversions
  // Validate existing children array structure before preserving IDs
  let existingChildren =
    'children' in html && Array.isArray(html.children)
      ? (html.children as Array<Record<string, unknown>>)
      : null

  // Validate children[0] is {} (legacy spec requirement)
  // If structure is invalid, don't preserve IDs to avoid corruption
  if (
    existingChildren &&
    existingChildren.length > 0 &&
    Object.keys(existingChildren[0] || {}).length > 0
  ) {
    // Don't preserve IDs if structure is invalid (children[0] must be empty {})
    existingChildren = null
  }

  // CRITICAL: Always initialize children with [{}] FIRST (per legacy team recommendation)
  // Legacy spec requires children to always start with [{}] (empty object)
  // This must be set explicitly before ensureRequiredHtmlFields to guarantee it exists
  html['children'] = [{}]

  // Then add options if they exist (this will replace the [{}] with [{}] + options)
  if (
    'options' in nodeData &&
    Array.isArray(nodeData.options) &&
    nodeData.options.length > 0
  ) {
    const options = nodeData.options as Array<Record<string, unknown>>
    html['children'] = convertOptionsToChildren(
      options,
      nodeData as Record<string, unknown>,
      nodeId,
      (option, child, index) => {
        if ('weight' in option && option.weight !== undefined) {
          // CRITICAL: Use explicit String() conversion (not type coercion)
          // Legacy spec requires weight_id to be a string (e.g., "50", not 50)
          child['weight_id'] = String(option.weight)
        } else {
          // Legacy spec: default empty weight_id to index + 1 (matching Legacy behavior)
          // File: edit_switchboard.php:7721 - 'weight_id":' + (val != '' ? ... : '"' + (index + 1) + '"')
          // This ensures 100% compatibility with Legacy, even though Admin3 hook typically
          // ensures weights are always present (clamped to 1-100 on blur)
          child['weight_id'] = String((index ?? 0) + 1)
        }
        // Preserve option ID from existing children array if available (for round-trip)
        // Match by index (existingChildren[0] is {}, so use index + 1)
        // Priority order: option.id > existingChildren[index+1].id > generated ID
        // CRITICAL: Check both !child['id'] AND !option.id to avoid overwriting option.id
        // convertOptionsToChildren will check option.id after fieldMapper, so we must not set
        // from existingChildren if option.id exists
        if (
          existingChildren &&
          index !== undefined &&
          existingChildren.length > index + 1 &&
          existingChildren[index + 1] &&
          typeof existingChildren[index + 1] === 'object' &&
          'id' in existingChildren[index + 1] &&
          !child['id'] && // Only preserve if child doesn't already have an ID
          !('id' in option && option.id) // Don't preserve if option has explicit ID (takes priority)
        ) {
          // CRITICAL: Normalize option ID to remove node type prefix for call_distribution
          // Legacy Admin's save condition splits by '_' and expects numeric node ID as first part
          // Note: call_distribution already uses correct format, but normalize to be safe
          const existingId = String(existingChildren[index + 1].id)
          child['id'] = normalizeOptionId(existingId, 'call_distribution')
        }
      },
      'call_distribution',
    )

    // Remove options array after converting to children
    delete html['options']
  }

  const kind = NODE_TYPE_TO_KIND_MAP[NodeType.CallDistribution]
  const result = ensureRequiredHtmlFields(kind, html) as CallDistributionHtml
  // CRITICAL: Defensive check - ensure children always starts with [{}] (per legacy team recommendation)
  // ensureRequiredHtmlFields should not overwrite existing children, but verify structure
  if (!Array.isArray(result['children']) || result['children'].length === 0) {
    result['children'] = [{}]
  } else if (
    result['children'][0] &&
    typeof result['children'][0] === 'object' &&
    Object.keys(result['children'][0] as Record<string, unknown>).length > 0
  ) {
    // If first child is not empty, prepend [{}] (per legacy team recommendation)
    result['children'] = [{}, ...result['children']]
  }
  // CRITICAL: Ensure timeout is always a string (legacy spec requirement)
  // CRITICAL: Preserve null if explicitly set (for strict conformity when loading legacy data)
  // Only default to '20' when missing, not when explicitly null
  if ('timeout' in result) {
    if (result['timeout'] !== null && result['timeout'] !== undefined) {
      ;(result as Record<string, unknown>)['timeout'] = String(
        result['timeout'],
      )
    }
    // If timeout is null, preserve it (don't overwrite)
  } else {
    ;(result as Record<string, unknown>)['timeout'] = '20' // Only default when missing
  }
  // CRITICAL: Ensure all weight_id values in children are strings
  // Also remove Admin3-only 'target' field from children (not part of Legacy format)
  if ('children' in result && Array.isArray(result['children'])) {
    result['children'].forEach((child, index) => {
      if (index > 0 && child && typeof child === 'object') {
        const childObj = child as Record<string, unknown>
        if ('weight_id' in childObj && childObj['weight_id'] !== undefined) {
          // Legacy spec requires weight_id to be a string (e.g., "50", not 50)
          childObj['weight_id'] = String(childObj['weight_id'])
        }
        // CRITICAL: Remove 'target' field - this is Admin3-only and not in Legacy format
        // Target information is stored in endpoints, not in children array items
        if ('target' in childObj) {
          delete childObj['target']
        }
      }
    })
  }
  // Remove Admin3-only fields: options array
  deleteAdmin3OnlyFields(result, ['options'])

  return result
}

/**
 * Convert interactive webhook node data to legacy HTML format
 */
export function convertInteractiveWebhookNodeData(
  nodeData: NodeData,
  nodeId?: string,
): InteractiveWebhookHtml {
  const html = baseConvertNodeDataToHtml(
    nodeData as Record<string, unknown>,
    NodeType.InteractiveWebhook,
    nodeId,
  )

  // iwebhook_url should save the URL string when provided, or null if empty
  // Legacy code (line 7768) saves URL string if provided, null otherwise
  // baseConvertNodeDataToHtml converts 'url' → 'url' (snake_case), so we need to check both
  if ('url' in nodeData && nodeData.url) {
    html['iwebhook_url'] = String(nodeData.url)
  } else if ('url' in html && html.url) {
    html['iwebhook_url'] = String(html.url)
  } else {
    html['iwebhook_url'] = null
  }
  // Remove 'url' field if it exists (should have been converted to iwebhook_url, but safety check)
  if ('url' in html && html['url'] !== 'iwebhook_url') {
    delete html['url']
  }

  if ('allowExtensionAsOption' in nodeData) {
    // CRITICAL: Use explicit Boolean() conversion (not type coercion)
    html['iwebhook_allow_extension_dialing'] = Boolean(
      nodeData.allowExtensionAsOption,
    )
    // Remove allow_extension_as_option if it was added by baseConvertNodeDataToHtml (shouldn't happen now, but safety net)
    delete html['allow_extension_as_option']
  }

  // CRITICAL: Preserve option IDs from existing HTML children (for round-trip scenarios)
  // If html already has children from a previous conversion, preserve their IDs
  // This ensures option IDs remain stable across round-trip conversions
  // Validate existing children array structure before preserving IDs
  let existingChildren =
    'children' in html && Array.isArray(html.children)
      ? (html.children as Array<Record<string, unknown>>)
      : null

  // Validate children[0] is {} (legacy spec requirement)
  // If structure is invalid, don't preserve IDs to avoid corruption
  if (
    existingChildren &&
    existingChildren.length > 0 &&
    Object.keys(existingChildren[0] || {}).length > 0
  ) {
    // Don't preserve IDs if structure is invalid (children[0] must be empty {})
    existingChildren = null
  }

  // CRITICAL: Always initialize children with [{}] FIRST (per legacy team recommendation)
  // Legacy spec requires children to always start with [{}] (empty object)
  // This must be set explicitly before ensureRequiredHtmlFields to guarantee it exists
  html['children'] = [{}]

  // Then add options if they exist (this will replace the [{}] with [{}] + options)
  if (
    'options' in nodeData &&
    Array.isArray(nodeData.options) &&
    nodeData.options.length > 0
  ) {
    const options = nodeData.options as Array<Record<string, unknown>>
    html['children'] = convertOptionsToChildren(
      options,
      nodeData as Record<string, unknown>,
      nodeId,
      (option, child, index) => {
        if ('value' in option && option.value) {
          // CRITICAL: Use explicit String() conversion
          child['option_id'] = String(option.value)
        }
        // Preserve option ID from existing children array if available (for round-trip)
        // Match by index (existingChildren[0] is {}, so use index + 1)
        // Priority order: option.id > existingChildren[index+1].id > generated ID
        // CRITICAL: Check both !child['id'] AND !option.id to avoid overwriting option.id
        // convertOptionsToChildren will check option.id after fieldMapper, so we must not set
        // from existingChildren if option.id exists
        if (
          existingChildren &&
          index !== undefined &&
          existingChildren.length > index + 1 &&
          existingChildren[index + 1] &&
          typeof existingChildren[index + 1] === 'object' &&
          'id' in existingChildren[index + 1] &&
          !child['id'] && // Only preserve if child doesn't already have an ID
          !('id' in option && option.id) // Don't preserve if option has explicit ID (takes priority)
        ) {
          // CRITICAL: Normalize option ID to remove node type prefix for interactive_webhook
          // Legacy Admin's save condition splits by '_' and expects numeric node ID as first part
          const existingId = String(existingChildren[index + 1].id)
          child['id'] = normalizeOptionId(existingId, 'interactive_webhook')
        }
      },
      'interactive_webhook',
    )
    // Remove options array after converting to children
    delete html['options']
  }

  const kind = NODE_TYPE_TO_KIND_MAP[NodeType.InteractiveWebhook]
  const result = ensureRequiredHtmlFields(kind, html) as InteractiveWebhookHtml
  // CRITICAL: Defensive check - ensure children always starts with [{}] (per legacy team recommendation)
  // ensureRequiredHtmlFields should not overwrite existing children, but verify structure
  if (!Array.isArray(result['children']) || result['children'].length === 0) {
    result['children'] = [{}]
  } else if (
    result['children'][0] &&
    typeof result['children'][0] === 'object' &&
    Object.keys(result['children'][0] as Record<string, unknown>).length > 0
  ) {
    // If first child is not empty, prepend [{}] (per legacy team recommendation)
    result['children'] = [{}, ...result['children']]
  }
  // CRITICAL: Ensure all option_id values in children are strings
  if ('children' in result && Array.isArray(result['children'])) {
    result['children'].forEach((child, index) => {
      if (index > 0 && child && typeof child === 'object') {
        const childObj = child as Record<string, unknown>
        if ('option_id' in childObj && childObj['option_id'] !== undefined) {
          // Legacy spec requires option_id to be a string (e.g., "1", not 1)
          childObj['option_id'] = String(childObj['option_id'])
        }
      }
    })
  }
  // CRITICAL: Ensure iwebhook_allow_extension_dialing is boolean (not number or string)
  if ('iwebhook_allow_extension_dialing' in result) {
    result['iwebhook_allow_extension_dialing'] = Boolean(
      result['iwebhook_allow_extension_dialing'],
    )
  }
  // Final cleanup: ensure Admin3-only fields are removed
  deleteAdmin3OnlyFields(result, ['options', 'allow_extension_as_option'])
  // P1-6 Fix: CRITICAL - Preserve iwebhook_url if provided (defensive check)
  // iwebhook_url should preserve the URL value if provided (already set above)
  // Ensure it's a string if provided, or null if empty
  // Double-check in case ensureRequiredHtmlFields or something else overwrote it
  if ('iwebhook_url' in result && result['iwebhook_url'] !== null) {
    result['iwebhook_url'] = String(result['iwebhook_url'])
  } else if ('url' in nodeData && nodeData.url) {
    // Edge case: URL exists in nodeData but iwebhook_url wasn't set correctly
    result['iwebhook_url'] = String(nodeData.url)
  } else {
    result['iwebhook_url'] = null
  }
  return result
}

/**
 * Convert day/night node data to legacy HTML format
 */
export function convertDayNightNodeData(
  nodeData: NodeData,
  nodeId?: string,
): DayNightHtml {
  const html = baseConvertNodeDataToHtml(
    nodeData as Record<string, unknown>,
    NodeType.DayNight,
    nodeId,
  )

  // Map toggleCode to choice_id per legacy code logic (line 7648):
  // - null/undefined → null (Advanced mode, uses *3)
  // - "*30" → "30" (Basic mode, extract number)
  // - "30" → "30" (Basic mode, already without asterisk)
  // Legacy code shows choice_id CAN be set when in Basic mode with toggle code selected
  // Even though saved data often shows null, the code logic supports saving values
  const dayNightData = nodeData as DayNightNodeData
  const toggleCode = dayNightData.toggleCode

  if (toggleCode === null || toggleCode === undefined) {
    html['choice_id'] = null
  } else if (typeof toggleCode === 'string') {
    // Extract number from toggle code (remove asterisk if present)
    // e.g., "*30" → "30", "30" → "30"
    const codeValue = toggleCode.startsWith('*')
      ? toggleCode.substring(1)
      : toggleCode
    html['choice_id'] = codeValue
  } else {
    html['choice_id'] = null
  }

  // Always remove Admin3-only fields (safety net - shouldn't be added now, but remove if present)
  delete html['toggle_code']
  delete html['status']

  const kind = NODE_TYPE_TO_KIND_MAP[NodeType.DayNight]
  const result = ensureRequiredHtmlFields(kind, html) as DayNightHtml
  // Final cleanup: ensure Admin3-only fields are removed
  deleteAdmin3OnlyFields(result, ['toggle_code', 'status'])
  return result
}

/**
 * Convert transfer rollover node data to legacy HTML format
 */
export function convertTransferRolloverNodeData(
  nodeData: NodeData,
  nodeId?: string,
): TransferRolloverHtml {
  const html = baseConvertNodeDataToHtml(
    nodeData as Record<string, unknown>,
    NodeType.TransferAndRollover,
    nodeId,
  )

  if ('extensionId' in nodeData && nodeData.extensionId) {
    html['transfer_rollover_id'] = String(nodeData.extensionId)
    delete html['extension_id']
  } else if (
    'extensions' in nodeData &&
    Array.isArray(nodeData.extensions) &&
    nodeData.extensions.length > 0
  ) {
    html['transfer_rollover_id'] = String(nodeData.extensions[0])
    // Note: extensions array is Admin3-only, not saved in Legacy format
  }

  const kind = NODE_TYPE_TO_KIND_MAP[NodeType.TransferAndRollover]
  const result = ensureRequiredHtmlFields(kind, html) as TransferRolloverHtml
  // Remove Admin3-only fields: extension_id and extensions array
  deleteAdmin3OnlyFields(result, ['extensions', 'extension_id'])
  return result
}

/**
 * Convert change caller ID node data to legacy HTML format
 */
export function convertChangeCallerIdNodeData(
  nodeData: NodeData,
  nodeId?: string,
): ChangeCallerIdHtml {
  const html = baseConvertNodeDataToHtml(
    nodeData as Record<string, unknown>,
    NodeType.ChangeCallerId,
    nodeId,
  )

  // Handle mode/newCallerIdOption conversion BEFORE ensureRequiredHtmlFields.
  // Priority: mode > newCallerIdOption.
  // mode reflects the current Admin2 radio selection; newCallerIdOption may be
  // stale raw legacy data from the initial load.
  // mode is skipped in baseConvertNodeDataToHtml, so we need to handle it directly.
  let mappedFromMode = false
  if ('mode' in nodeData && nodeData.mode) {
    const mode = nodeData.mode
    if (mode === 'replace') {
      // Legacy save value for "Replace Caller ID".
      html['new_caller_id_option'] = 'replace_caller'
      mappedFromMode = true
    } else if (
      mode === 'add_front' ||
      mode === 'prefix' ||
      mode === 'six_char'
    ) {
      // Legacy save value for "Add up to 20 characters to the front".
      html['new_caller_id_option'] = 'six_char'
      mappedFromMode = true
    }
  }

  if (!mappedFromMode && 'newCallerIdOption' in nodeData) {
    // If newCallerIdOption is explicitly null, preserve it (legacy can save null).
    if (nodeData.newCallerIdOption === null) {
      html['new_caller_id_option'] = null
    } else if (nodeData.newCallerIdOption) {
      const option = nodeData.newCallerIdOption
      if (option === 'replace_caller' || option === 'six_char') {
        html['new_caller_id_option'] =
          option === 'replace_caller' ? 'replace_caller' : 'six_char'
      } else if (option === 'prefix' || option === 'add_front') {
        html['new_caller_id_option'] = 'six_char'
      }
    }
  }
  // If neither exists, ensureRequiredHtmlFields will set default to 'six_char'

  handleMinimizedField(nodeData, html)

  const kind = NODE_TYPE_TO_KIND_MAP[NodeType.ChangeCallerId]
  const result = ensureRequiredHtmlFields(kind, html) as ChangeCallerIdHtml

  // CRITICAL: Only default to 'six_char' if the field is missing or undefined (not if it's explicitly null)
  // Legacy CAN save null (when no radio button is checked), so we should preserve null for conformity
  if (
    !('new_caller_id_option' in result) ||
    (result.new_caller_id_option !== null &&
      result.new_caller_id_option !== undefined &&
      !result.new_caller_id_option)
  ) {
    result.new_caller_id_option = 'six_char'
  }

  // Ensure mode is removed (safety net - shouldn't be present since it's skipped)
  deleteAdmin3OnlyFields(result, ['mode'])
  return result
}

/**
 * Convert webhook node data to legacy HTML format
 */
export function convertWebhookNodeData(
  nodeData: NodeData,
  nodeId?: string,
): WebhookHtml {
  const html = baseConvertNodeDataToHtml(
    nodeData as Record<string, unknown>,
    NodeType.Webhook,
    nodeId,
  )

  // webhook_id should match the node ID (matching legacy behavior)
  // Legacy saves webhook_id as the node ID (13-digit number)
  // Extract numeric ID if nodeId contains kind prefix (e.g., "webhook-123456789" → "123456789")
  if (nodeId) {
    html['webhook_id'] = extractNumericId(nodeId)
  } else {
    // Fallback to null if nodeId is not provided (should not happen in normal operation)
    html['webhook_id'] = null
  }

  // P1-5 Fix: CRITICAL - Preserve notify_url if provided
  // Legacy saves URL string when provided, null if empty (line 7687)
  // baseConvertNodeDataToHtml converts 'url' → 'notify_url' via special handling (lines 54-60)
  // But we need to ensure it's preserved even if it exists
  if ('notify_url' in html && html['notify_url']) {
    // URL was already converted by baseConvertNodeDataToHtml, preserve it
    html['notify_url'] = String(html['notify_url'])
  } else if ('url' in nodeData && nodeData.url) {
    // URL exists in nodeData but wasn't converted yet (edge case), convert it
    html['notify_url'] = String(nodeData.url)
  } else {
    // No URL provided, set to null (matching legacy behavior)
    html['notify_url'] = null
  }

  // Remove 'url' field if it exists (should have been converted to notify_url, but safety check)
  if ('url' in html) {
    delete html['url']
  }

  const kind = NODE_TYPE_TO_KIND_MAP[NodeType.Webhook]
  const result = ensureRequiredHtmlFields(kind, html) as WebhookHtml
  // Ensure webhook_id is set to node ID (not overwritten by ensureRequiredHtmlFields)
  if (nodeId) {
    result.webhook_id = extractNumericId(nodeId)
  } else {
    result.webhook_id = null
  }
  // notify_url is already set above - preserve it (either URL string or null)
  return result
}
